package com.eladfinish.bible.contest.utils

/**
 * Platform-split implementations of the two ot-pasuk look-behind regexes:
 *
 * - pattern A ([MyStringUtils.getRegexOfOtiotPasukPlainSpace]): `(?<=(?:\]|פרק|׃)\s)\b([א-ת]{1,3})\b(\s)`
 * - pattern B ([getRegexOfOtiotPasuk]): `(?<=(?:\]|פרק(?:<ws>[א-ת]{1,3})?|׃)<ws>{1,2})\b([א-ת]{1,3})\b(<ws>)`
 *   where `<ws>` is `[\s NBSP]`; the optional chapter-number part of the trigger catches the
 *   first verse letter of a section that starts mid-chapter (haftarah: "פרק מב ה ...")
 *
 * On Android the actuals run these regexes exactly as before. On iOS, Kotlin/Native evaluates a
 * positive look-behind at every input position, which costs ~180 ms per pattern on a single
 * chapter (~360 ms of the ~370 ms it took to load one chapter of Tanakh browse). The iOS actuals
 * instead match the cheap inner pattern and verify the look-behind context manually — output is
 * byte-identical (guarded by ChapterPipelineCharacterizationTest and OtiotPasukPlatformTest,
 * which run on both platforms) and ~600x faster.
 */

/** The letters group (group 1) of a pattern-B match: the ot-pasuk letters without the trailing whitespace. */
data class OtiotPasukMatch(
    val letters: String,
    val lettersStart: Int,
    val lettersEndExclusive: Int,
)

/** Pattern A replaced with `"$1" + NO_BREAK_SPACE`: keep the ot-pasuk letters, turn the trailing space into NBSP. */
internal expect fun String.appendNbspAfterOtiotPasuk(): String

/** All pattern-B matches, in order. */
internal expect fun findOtiotPasukMatches(text: CharSequence): List<OtiotPasukMatch>

/**
 * Pattern B (the whole match: letters + trailing whitespace) replaced with a literal.
 * [literalReplacement] must not contain `$n` group references.
 */
internal expect fun String.replaceOtiotPasuk(literalReplacement: String): String

/** Pattern B replaced with `"($1)$2"`: wrap the ot-pasuk letters in parentheses, keep the trailing whitespace. */
internal expect fun String.parenthesizeOtiotPasuk(): String

/**
 * Two more look-behind patterns used by the tikun-korim flow (GetTikunKorimFormatUseCase), which
 * runs on whole parashot (~25K chars):
 *
 * - pattern C ([getRegexOfOtiotPasukAfterNunMenuzeret]): `(?<=׆\s)(?!פרק(?!WORD_CHAR))[א-ת]{1,3}`
 * - pattern D ([ALIYOT_AFTER_NEWLINE_REGEX]): `(?<=(?:\r?\n))\s\[.*?\]\s+`
 *
 * Pattern D's variable-length look-behind cost ~5 SECONDS per parasha on Kotlin/Native release
 * (fixed-length look-behinds like pattern C's are cheap, ~1 ms, but get the same treatment for
 * consistency). Both are replaced with `""` at their only call sites.
 */
internal const val ALIYOT_AFTER_NEWLINE_REGEX = "(?<=(?:\\r?\\n))\\s\\[.*?\\]\\s+"

/** Pattern C replaced with `""`: drop the ot-pasuk letters that follow a nun menuzeret. */
internal expect fun String.removeOtPasukAfterNunMenuzeret(): String

/** Pattern D replaced with `""`: drop `[aliyah]` markers (with surrounding whitespace) that start a line. */
internal expect fun String.removeAliyotMarkersAfterNewline(): String
